import React, { useState } from 'react';
import { ExtractedDocumentData } from '../../types';
import { CalendarIcon, BuildingOfficeIcon, MapPinIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { MoreVertical, Trash2, Check } from 'lucide-react';

import { typographyClasses } from '../../styles/typography';
import { cn } from '../../lib/utils';
import AutoResizeButton from '../ui/AutoResizeButton';
import { TalentPageCard, TalentPageCardContent } from '../ui/TalentPageCard';
import RadarChartCard from './RadarChartCard';
import TiltedCard from '../ui/TiltedCard';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Button } from '../ui/Button';
import TalRevAPI from '../../services/api';
import { DownloadConfirmationModal } from '../DownloadConfirmationModal';

interface RedesignedProfileCardProps {
  extractedData: ExtractedDocumentData;
  scoringResults?: any;
  className?: string;
  showRadarChart?: boolean;
  onDelete?: (id: string) => void;
  talentId?: string;
}

const RedesignedProfileCard: React.FC<RedesignedProfileCardProps> = ({
  extractedData,
  scoringResults,
  className = '',
  showRadarChart = true,
  onDelete,
  talentId
}) => {
  // Initialize hooks first (must be before any early returns)
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [aliasCopied, setAliasCopied] = useState(false);

  // Safety check for extractedData and employee_profile (after hooks)
  if (!extractedData || !extractedData.employee_profile) {
    console.error('RedesignedProfileCard: extractedData or employee_profile is missing', extractedData);
    return null;
  }

  const profile = extractedData.employee_profile;
  
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete || !talentId) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      await TalRevAPI.deleteTalentCard(talentId);
      onDelete(talentId);
    } catch (error) {
      console.error('Failed to delete talent:', error);
      setDeleteError('Failed to delete talent');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (!talentId) {
      console.error('No talent ID available for download');
      return;
    }

    setIsDownloading(true);
    try {
      const blob = await TalRevAPI.downloadOriginalFile(talentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `talent-card-${talentId}.pptx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowDownloadModal(false);
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyAlias = async () => {
    if (!profile.alias) return;

    try {
      await navigator.clipboard.writeText(profile.alias);
      setAliasCopied(true);
      setTimeout(() => setAliasCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy alias:', error);
    }
  };

  return (
    <>
      <div className={`grid gap-4 sm:gap-6 ${
        showRadarChart ? 'lg:grid-cols-2' : 'lg:grid-cols-1'
      } ${className}`}>
        {/* Profile Card */}
        <TalentPageCard className="overflow-hidden h-full">
          <div className="flex flex-col lg:flex-row h-full">
            {/* Top/Left Column - Image */}
            <div className="w-full lg:w-1/3 h-48 lg:h-full bg-gray-100 flex-shrink-0">
              <TiltedCard
                imageSrc="/image.png"
                altText={`${profile.name} profile`}
                containerHeight="100%"
                containerWidth="100%"
                scaleOnHover={1.05}
                rotateAmplitude={8}
                className="rounded-t-xl lg:rounded-t-none lg:rounded-l-xl"
                objectFit="contain"
              />
            </div>
            
            {/* Bottom/Right Column - Content */}
            <TalentPageCardContent className="flex-1 pl-3 sm:pl-4 lg:pl-6 pr-2 sm:pr-3 lg:pr-4 py-3 sm:py-4 min-w-0">
              {/* Header Section */}
              <div className="mb-6">
                <div className="mb-2 flex items-start justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <h1 className={typographyClasses.heading.h2}>{profile.name}</h1>
                    {profile.alias && (
                      <div className="flex items-center gap-0.5 text-gray-500">
                        <button
                          onClick={handleCopyAlias}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          title="Copy alias to clipboard"
                        >
                          {aliasCopied ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <span className={cn(typographyClasses.body.small, "text-gray-600")}>
                          {profile.alias.replace('@', '')}
                        </span>
                      </div>
                    )}
                  </div>
                  {onDelete && talentId && (
                    <div className="flex items-center space-x-2 ml-4">
                      {deleteError && (
                        <span className="text-red-500 text-[var(--font-size-sm)]">{deleteError}</span>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-gray-100"
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                            ) : (
                              <MoreVertical className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={handleDelete}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            disabled={isDeleting}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
                <p className={typographyClasses.body.large + " text-gray-600 mb-1"}>{profile.role}</p>
                <p className={typographyClasses.body.small + " text-gray-500"}>{profile.tenure} experience</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <BuildingOfficeIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${typographyClasses.label.uppercase} truncate`}>Level</p>
                <p className={`${typographyClasses.label.primary} truncate`}>{extractedData.employee_profile.level || 'L6'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${typographyClasses.label.uppercase} truncate`}>Tenure</p>
                <p className={`${typographyClasses.label.primary} truncate`}>{extractedData.employee_profile.tenure || 'N/A'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${typographyClasses.label.uppercase} truncate`}>TFCs</p>
                <p className={`${typographyClasses.label.primary} truncate`}>
                   {(() => {
                     const tfcs = extractedData.employee_profile.tfcs;
                     if (Array.isArray(tfcs)) {
                       return tfcs.join(', ');
                     } else if (typeof tfcs === 'string') {
                       try {
                         const parsed = JSON.parse(tfcs.replace(/'/g, '"'));
                         return Array.isArray(parsed) ? parsed.join(', ') : tfcs;
                       } catch {
                         return tfcs;
                       }
                     }
                     return 'Cloud Operations';
                   })()} 
                 </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${typographyClasses.label.uppercase} truncate`}>Certifications</p>
                <p className={`${typographyClasses.label.primary} truncate`} title={extractedData.employee_profile.certification_level}>
                  {extractedData.employee_profile.certification_level ? 
                    extractedData.employee_profile.certification_level.length > 30 ? 
                      extractedData.employee_profile.certification_level.substring(0, 30) + '...' : 
                      extractedData.employee_profile.certification_level 
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
          
              {/* Additional Info */}
              {extractedData.employee_profile.dynamic_fields && (
                <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-4 sm:mb-6">
              {extractedData.employee_profile.dynamic_fields.time_in_level && (
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`${typographyClasses.label.uppercase} truncate`}>Time in Level</p>
                    <p className={`${typographyClasses.label.primary} truncate`}>{extractedData.employee_profile.dynamic_fields.time_in_level}</p>
                  </div>
                </div>
              )}
            </div>
          )}



              {/* Contact Information */}
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
            <div className="flex items-center space-x-3 text-gray-700">
              <MapPinIcon className="w-5 h-5" />
              <span className={typographyClasses.body.small}>Seattle, WA</span>
            </div>
            
            <div className="flex items-center space-x-3 text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={typographyClasses.body.small}>9:00 AM - 5:00 PM PST</span>
            </div>
          </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
                <AutoResizeButton
                  variant="primary"
                  text="Talent card"
                  shortText="Card"
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2-2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  }
                  size="profile"
                  className="flex-1"
                  onClick={() => setShowDownloadModal(true)}
                />
                
                <AutoResizeButton
                  variant="secondary"
                  text="Phone tool"
                  shortText="Phone tool"
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  }
                  size="profile"
                  className="flex-1"
                  onClick={() => {
                    const alias = profile.alias;
                    if (alias) {
                      const cleanAlias = alias.replace('@', '');
                      window.open(`https://phonetool.amazon.com/users/${cleanAlias}`, '_blank', 'noopener,noreferrer');
                    }
                  }}
                />
              </div>
            </TalentPageCardContent>
          </div>
        </TalentPageCard>
        
        {/* Radar Chart Card */}
        {showRadarChart && (
          <TalentPageCard className="p-3 sm:p-4 lg:p-6 overflow-hidden h-full">
            <RadarChartCard extractedData={extractedData} scoringResults={scoringResults} />
          </TalentPageCard>
        )}
      </div>
      
      <DownloadConfirmationModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onConfirm={handleDownload}
        fileName={`talent-card-${talentId}.pptx`}
        isLoading={isDownloading}
      />
    </>
  );
};

export default RedesignedProfileCard;